import { useState } from 'react';
import { cmToDisplayUnit, displayUnitToCm } from '@/utils/unitConversion';
import {
  CORE_MEASUREMENT_FIELDS,
  OPTIONAL_MEASUREMENT_FIELDS,
  PREFERRED_FIT_LABEL,
  RISE_PREFERENCE_LABEL,
  type FitPassport,
  type KnownBrandSize,
  type PreferredFit,
  type RisePreference,
} from '@/types/passport';
import type { Audience } from '@/types/domain';

interface Props {
  initial: FitPassport;
  onSave: (passport: FitPassport) => void;
}

const AUDIENCE_OPTIONS: { value: Audience; label: string }[] = [
  { value: 'women', label: 'Women' },
  { value: 'men', label: 'Men' },
  { value: 'child', label: 'Youth (under 12)' },
];

function newKnownBrandSize(): KnownBrandSize {
  return { id: crypto.randomUUID(), brandName: '', category: 'tops', size: '' };
}

export function FitPassportForm({ initial, onSave }: Props) {
  const [draft, setDraft] = useState<FitPassport>(initial);

  function setMeasurement(key: string, displayValue: string) {
    const num = displayValue.trim() === '' ? undefined : Number(displayValue);
    setDraft((d) => ({
      ...d,
      measurements: {
        ...d.measurements,
        [key]: num === undefined || Number.isNaN(num) ? undefined : displayUnitToCm(num, d.preferredUnit),
      },
    }));
  }

  function displayValueFor(key: string): string {
    const cm = (draft.measurements as Record<string, number | undefined>)[key];
    return cm === undefined ? '' : String(cmToDisplayUnit(cm, draft.preferredUnit));
  }

  function toggleUnit(unit: 'cm' | 'in') {
    setDraft((d) => ({ ...d, preferredUnit: unit }));
  }

  const coreFilledCount = CORE_MEASUREMENT_FIELDS.filter(
    (f) => (draft.measurements as Record<string, number | undefined>)[f.key] !== undefined
  ).length;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
      className="space-y-6"
    >
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-ink">
          Display name <span className="font-normal text-muted">(optional — only shown back to you, e.g. as initials on your Profile icon)</span>
        </span>
        <input
          type="text"
          value={draft.displayName ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value || undefined }))}
          placeholder="Not set"
          maxLength={40}
          className="min-h-[44px] w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-base text-ink shadow-sm"
        />
      </label>

      <div>
        <span className="mb-2 block text-sm font-medium text-ink">Measurement unit</span>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Preferred unit">
          {(['cm', 'in'] as const).map((u) => (
            <button
              key={u}
              type="button"
              role="radio"
              aria-checked={draft.preferredUnit === u}
              onClick={() => toggleUnit(u)}
              className={`min-h-[44px] rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                draft.preferredUnit === u
                  ? 'border-brand bg-brand text-ink shadow-card'
                  : 'border-stone-200 bg-white text-ink hover:border-brand/40'
              }`}
            >
              {u === 'cm' ? 'Centimetres' : 'Inches'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium text-ink">Audience</span>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Passport audience">
          {AUDIENCE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={draft.audience === opt.value}
              onClick={() => setDraft((d) => ({ ...d, audience: opt.value }))}
              className={`min-h-[44px] rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                draft.audience === opt.value
                  ? 'border-brand bg-brand text-ink shadow-card'
                  : 'border-stone-200 bg-white text-ink hover:border-brand/40'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <span className="block text-sm font-medium text-ink">Core measurements</span>
          <span className="text-xs text-muted">{coreFilledCount} of {CORE_MEASUREMENT_FIELDS.length} set</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {CORE_MEASUREMENT_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="mb-1 block text-xs text-muted">{f.label}</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step={0.1}
                value={displayValueFor(f.key)}
                onChange={(e) => setMeasurement(f.key, e.target.value)}
                placeholder={draft.preferredUnit}
                className="min-h-[44px] w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-base text-ink shadow-sm"
              />
            </label>
          ))}
        </div>
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium text-ink">
          Optional measurements <span className="font-normal text-muted">(only if you know them)</span>
        </span>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {OPTIONAL_MEASUREMENT_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="mb-1 block text-xs text-muted">{f.label}</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step={0.1}
                value={displayValueFor(f.key)}
                onChange={(e) => setMeasurement(f.key, e.target.value)}
                placeholder={draft.preferredUnit}
                className="min-h-[44px] w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-base text-ink shadow-sm"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Preferred fit</span>
          <select
            value={draft.preferredFit ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, preferredFit: (e.target.value || null) as PreferredFit | null }))}
            className="min-h-[44px] w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-base text-ink shadow-sm"
          >
            <option value="">Not set</option>
            {(Object.keys(PREFERRED_FIT_LABEL) as PreferredFit[]).map((k) => (
              <option key={k} value={k}>
                {PREFERRED_FIT_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Rise preference</span>
          <select
            value={draft.risePreference ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, risePreference: (e.target.value || null) as RisePreference | null }))}
            className="min-h-[44px] w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-base text-ink shadow-sm"
          >
            <option value="">Not set</option>
            {(Object.keys(RISE_PREFERENCE_LABEL) as RisePreference[]).map((k) => (
              <option key={k} value={k}>
                {RISE_PREFERENCE_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium text-ink">
          Brands and sizes that already fit you well <span className="font-normal text-muted">(optional)</span>
        </span>
        <div className="space-y-2">
          {draft.knownBrandSizes.map((row, i) => (
            <div key={row.id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2">
              <input
                type="text"
                value={row.brandName}
                onChange={(e) => {
                  const brandName = e.target.value;
                  setDraft((d) => ({
                    ...d,
                    knownBrandSizes: d.knownBrandSizes.map((r, ri) => (ri === i ? { ...r, brandName } : r)),
                  }));
                }}
                placeholder="Brand"
                className="min-h-[44px] rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-ink shadow-sm"
              />
              <select
                value={row.category}
                onChange={(e) => {
                  const category = e.target.value as KnownBrandSize['category'];
                  setDraft((d) => ({
                    ...d,
                    knownBrandSizes: d.knownBrandSizes.map((r, ri) => (ri === i ? { ...r, category } : r)),
                  }));
                }}
                className="min-h-[44px] rounded-xl border border-stone-200 bg-white px-2 py-2 text-sm text-ink shadow-sm"
              >
                <option value="tops">Tops</option>
                <option value="bottoms">Bottoms</option>
              </select>
              <input
                type="text"
                value={row.size}
                onChange={(e) => {
                  const size = e.target.value;
                  setDraft((d) => ({
                    ...d,
                    knownBrandSizes: d.knownBrandSizes.map((r, ri) => (ri === i ? { ...r, size } : r)),
                  }));
                }}
                placeholder="Size"
                className="min-h-[44px] w-20 rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-ink shadow-sm"
              />
              <button
                type="button"
                aria-label="Remove this brand/size"
                onClick={() => setDraft((d) => ({ ...d, knownBrandSizes: d.knownBrandSizes.filter((_, ri) => ri !== i) }))}
                className="min-h-[44px] min-w-[44px] rounded-xl border border-stone-200 text-muted hover:text-danger"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setDraft((d) => ({ ...d, knownBrandSizes: [...d.knownBrandSizes, newKnownBrandSize()] }))}
            className="min-h-[44px] w-full rounded-xl border border-dashed border-stone-300 text-sm font-medium text-muted hover:border-brand/40 hover:text-brand"
          >
            + Add a brand and size
          </button>
        </div>
      </div>

      <button
        type="submit"
        className="min-h-[44px] w-full rounded-xl bg-brand px-4 py-3 text-base font-semibold text-ink shadow-card sm:w-auto sm:px-8"
      >
        Save FitMap Passport
      </button>
    </form>
  );
}
